/**
 * <BilingualTextWithTranslate /> — EN + AR text inputs side by side
 * with AI ghost-text translation (M21.11, criterion 16).
 *
 * When the user types in one side and leaves the other empty, after
 * a 1.5s pause the empty side gets a suggested translation from
 * `ai_service.translate` (via POST /api/ai-assist/translate). The
 * user can accept it (Tab while focused on the empty field, or
 * click "Use"), edit it, or ignore it. A field filled from an AI
 * suggestion is marked with the <AIBadge /> per the DESIGN_SYSTEM
 * commandment.
 *
 * Translation needs a legal entity to resolve the tenant's Anthropic
 * key — with `legalEntityId` undefined the component degrades
 * gracefully to two plain inputs.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "../../lib/api";
import { AIBadge } from "./AIBadge";

const _DEBOUNCE_MS = 1500;

type Side = "en" | "ar";

interface Suggestion {
  side: Side;
  text: string;
}

export interface BilingualTextWithTranslateProps {
  valueEn: string;
  valueAr: string;
  onChangeEn: (v: string) => void;
  onChangeAr: (v: string) => void;
  legalEntityId?: string;
  labelEn?: string;
  labelAr?: string;
  /** Disambiguates the translation (e.g. "a job title"). */
  contextHint?: string;
  required?: boolean;
  placeholderEn?: string;
  placeholderAr?: string;
}

export function BilingualTextWithTranslate({
  valueEn,
  valueAr,
  onChangeEn,
  onChangeAr,
  legalEntityId,
  labelEn = "Name (English)",
  labelAr = "Name (Arabic)",
  contextHint,
  required,
  placeholderEn,
  placeholderAr,
}: BilingualTextWithTranslateProps) {
  const { t } = useTranslation();
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [aiFilled, setAiFilled] = useState<{ en: boolean; ar: boolean }>({
    en: false,
    ar: false,
  });
  const [error, setError] = useState<string | null>(null);
  const reqSeq = useRef(0);

  // Debounced ghost-text translation. Only fires when exactly one
  // side has content and a legal entity is available.
  useEffect(() => {
    setError(null);
    if (!legalEntityId) return;
    const en = valueEn.trim();
    const ar = valueAr.trim();

    let direction: "en_to_ar" | "ar_to_en" | null = null;
    let source = "";
    if (en && !ar) {
      direction = "en_to_ar";
      source = en;
    } else if (ar && !en) {
      direction = "ar_to_en";
      source = ar;
    }
    if (!direction) {
      setSuggestion(null);
      return;
    }

    const seq = ++reqSeq.current;
    const targetSide: Side = direction === "en_to_ar" ? "ar" : "en";
    const timer = setTimeout(async () => {
      try {
        const res = await api.aiTranslate({
          text: source,
          direction,
          legal_entity_id: legalEntityId,
          context_hint: contextHint,
        });
        // Ignore stale responses (the user kept typing).
        if (seq !== reqSeq.current) return;
        if (res.translation.trim()) {
          setSuggestion({ side: targetSide, text: res.translation.trim() });
        }
      } catch (e: unknown) {
        if (seq !== reqSeq.current) return;
        setError(
          e instanceof ApiError ? e.detail : "Translation unavailable",
        );
      }
    }, _DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [valueEn, valueAr, legalEntityId, contextHint]);

  function accept(side: Side) {
    if (!suggestion || suggestion.side !== side) return;
    if (side === "ar") onChangeAr(suggestion.text);
    else onChangeEn(suggestion.text);
    setAiFilled((p) => ({ ...p, [side]: true }));
    setSuggestion(null);
  }

  function fieldChange(side: Side, v: string) {
    // A manual edit clears the AI-filled marker for that side.
    setAiFilled((p) => (p[side] ? { ...p, [side]: false } : p));
    if (side === "en") onChangeEn(v);
    else onChangeAr(v);
  }

  function renderField(
    side: Side,
    label: string,
    value: string,
    placeholder: string | undefined,
    dir: "ltr" | "rtl",
  ) {
    const hasSuggestion = suggestion?.side === side;
    return (
      <label className="block text-sm">
        <span className="flex items-center gap-1.5 font-medium text-slate-700">
          {label}
          {required && <span className="text-rose-500">*</span>}
          {aiFilled[side] && <AIBadge />}
        </span>
        <input
          type="text"
          dir={dir}
          value={value}
          placeholder={hasSuggestion ? suggestion!.text : placeholder}
          onChange={(e) => fieldChange(side, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Tab" && hasSuggestion && !value.trim()) {
              e.preventDefault();
              accept(side);
            }
          }}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400"
        />
        {hasSuggestion && (
          <span className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
            <AIBadge />
            <span className="truncate" dir={dir}>
              {suggestion!.text}
            </span>
            <button
              type="button"
              onClick={() => accept(side)}
              className="ms-auto shrink-0 rounded border border-slate-300 px-1.5 py-0.5 font-medium text-slate-600 hover:bg-slate-50"
            >
              {t("app.bt_tab_to_use")}
            </button>
          </span>
        )}
      </label>
    );
  }

  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2">
        {renderField("en", labelEn, valueEn, placeholderEn, "ltr")}
        {renderField("ar", labelAr, valueAr, placeholderAr, "rtl")}
      </div>
      {error && <p className="mt-1 text-xs text-amber-700">{error}</p>}
    </div>
  );
}
